/**
 * 동의어 사전 관리 (Power User UI).
 *
 * hs_search_synonyms CRUD — term ↔ synonym 양방향 매핑.
 * pgroonga FTS expand_synonyms() 에서 양방향 lookup → 정밀 검색 ON 시 자동 expansion.
 *
 * 사용 흐름:
 *  1) 관세사가 도메인 어휘 (예: 한글 "전자거래" ↔ 영문 "e-commerce") 추가
 *  2) /precedents FTS 검색 시 두 단어 모두 자동 매치
 *  3) 도메인 (33-화장품 / 42-가죽 / ...) 으로 그룹화하여 관리
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Plus,
  Trash2,
  Save,
  X,
  ArrowLeft,
  RotateCcw,
  Search,
  HelpCircle,
} from "lucide-react";
import { useLang } from "./i18n";
import AuditSideNav from "./AuditSideNav";
import "./theme.css";

const EMPTY_DRAFT = { term: "", synonym: "", domain: "", note: "" };

export default function SynonymsPage() {
  const { t, lang, setLang } = useLang();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // 필터
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("");
  const [domains, setDomains] = useState([]);
  const [stats, setStats] = useState(null);

  // editor
  const [editing, setEditing] = useState(null); // id 또는 'new'
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (domain) params.set("domain", domain);
      params.set("limit", "1000");
      const r = await fetch(`/api/hs-synonyms?${params}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(String(e.message || e));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, domain]);

  const fetchMeta = useCallback(async () => {
    try {
      const [dr, sr] = await Promise.all([
        fetch("/api/hs-synonyms/domains").then((r) => r.json()),
        fetch("/api/hs-synonyms/stats").then((r) => r.json()),
      ]);
      setDomains(dr.domains || []);
      setStats(sr);
    } catch (_) {
      /* ignore */
    }
  }, []);

  // 검색 debounce 300ms
  useEffect(() => {
    const t = setTimeout(fetchRows, 300);
    return () => clearTimeout(t);
  }, [fetchRows]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  const startNew = () => {
    setEditing("new");
    setDraft({ ...EMPTY_DRAFT, domain: domain || "" });
  };
  const startEdit = (row) => {
    setEditing(row.id);
    setDraft({
      term: row.term,
      synonym: row.synonym,
      domain: row.domain || "",
      note: row.note || "",
    });
  };
  const cancelEdit = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  };

  const save = async () => {
    const body = {
      term: draft.term.trim(),
      synonym: draft.synonym.trim(),
      domain: draft.domain.trim() || null,
      note: draft.note.trim() || null,
    };
    if (!body.term || !body.synonym) {
      setErr(t("synonyms.required"));
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const url =
        editing === "new" ? "/api/hs-synonyms" : `/api/hs-synonyms/${editing}`;
      const method = editing === "new" ? "POST" : "PUT";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        let m = `HTTP ${r.status}`;
        try {
          m = (await r.json())?.detail || m;
        } catch (_) {}
        throw new Error(m);
      }
      cancelEdit();
      await Promise.all([fetchRows(), fetchMeta()]);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (
      !window.confirm(
        t("synonyms.delete.confirm", {
          term: row.term,
          synonym: row.synonym,
          id: row.id,
        }),
      )
    )
      return;
    setErr(null);
    try {
      const r = await fetch(`/api/hs-synonyms/${row.id}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await Promise.all([fetchRows(), fetchMeta()]);
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  // 그룹화 (domain 별)
  const grouped = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const d = r.domain || "(미분류)";
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(r);
    });
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        background: "var(--audit-bg)",
      }}
    >
      <AuditSideNav />
      <div
        className="audit-root"
        style={{
          flex: 1,
          minWidth: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "var(--audit-bg)",
          color: "var(--audit-text)",
        }}
      >
        {/* 상단 바 */}
        <div
          style={{
            padding: "10px 16px",
            borderBottom: "1px solid var(--audit-border)",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button
            onClick={() => {
              window.history.pushState({}, "", "/?audit=hs-hub");
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
            title="HS 분류 Hub"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "transparent",
              border: "1px solid var(--audit-border)",
              borderRadius: 4,
              padding: "3px 8px",
              cursor: "pointer",
              fontSize: 12,
              color: "var(--audit-text-muted)",
              fontFamily: "inherit",
            }}
          >
            <ArrowLeft size={12} /> {t("common.main")}
          </button>
          <span style={{ color: "var(--audit-text-faint)", fontSize: 12 }}>
            /
          </span>
          <h1 style={{ fontSize: 14, margin: 0, fontWeight: 600 }}>
            {t("synonyms.title")}
          </h1>
          <span
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              fontFamily: "JetBrains Mono, monospace",
            }}
          >
            {t("synonyms.total", {
              n: total.toLocaleString(),
              total: stats?.total?.toLocaleString() || "?",
            })}
          </span>
          <div style={{ flex: 1 }} />
          <div style={{ position: "relative" }}>
            <Search
              size={12}
              style={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--audit-text-muted)",
              }}
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("synonyms.search.placeholder")}
              style={{
                padding: "5px 10px 5px 26px",
                fontSize: 12,
                border: "1px solid var(--audit-border-strong)",
                background: "var(--audit-bg)",
                color: "var(--audit-text)",
                borderRadius: 6,
                width: 280,
                fontFamily: "inherit",
              }}
            />
          </div>
          <select
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            style={{
              padding: "5px 8px",
              fontSize: 12,
              border: "1px solid var(--audit-border-strong)",
              background: "var(--audit-bg)",
              color: "var(--audit-text)",
              borderRadius: 6,
              fontFamily: "inherit",
            }}
            title={t("synonyms.domain.title")}
          >
            <option value="">{t("synonyms.domain.all")}</option>
            {domains.map((d) => (
              <option key={d} value={d}>
                {d}
                {stats?.by_domain?.[d] != null
                  ? ` (${stats.by_domain[d]})`
                  : ""}
              </option>
            ))}
          </select>
          <button
            className="audit-btn audit-btn-primary"
            onClick={startNew}
            disabled={editing !== null}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
            title={t("synonyms.add.title")}
          >
            <Plus size={13} /> {t("common.add")}
          </button>
          <button
            className="audit-btn"
            onClick={() => setHelpOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            <HelpCircle size={13} /> {t("common.help")}
          </button>
          <button
            className="audit-btn"
            onClick={() => {
              setQ("");
              setDomain("");
            }}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            <RotateCcw size={13} /> {t("common.reset")}
          </button>
          <button
            className="audit-btn"
            onClick={() => setLang(lang === "ko" ? "en" : "ko")}
            title={`Switch language (current: ${lang.toUpperCase()})`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontFamily: "JetBrains Mono, monospace",
              fontWeight: 600,
            }}
          >
            {lang.toUpperCase()}
          </button>
        </div>

        {err && (
          <div
            style={{
              padding: "8px 16px",
              background: "rgba(217,48,37,0.08)",
              color: "#d93025",
              fontSize: 12,
              borderBottom: "1px solid #d93025",
            }}
          >
            {t("common.error")}: {err}
          </div>
        )}

        {/* 승인 대기 후보(자동 채굴: 제안 LLM 법령어 품명 · 결정례 검색 오타 교정) — 승인해야 사전에 들어간다(2026-09-15). */}
        <PendingCandidates onChanged={() => { fetchRows(); }} />
        {/* 본문 */}
        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {/* 새 항목 editor */}
          {editing === "new" && (
            <DraftEditor
              draft={draft}
              setDraft={setDraft}
              domains={domains}
              onSave={save}
              onCancel={cancelEdit}
              saving={saving}
              isNew
            />
          )}

          {loading && rows.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                color: "var(--audit-text-muted)",
                fontSize: 13,
              }}
            >
              {t("common.loading")}
            </div>
          ) : rows.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                color: "var(--audit-text-muted)",
                fontSize: 13,
              }}
            >
              {t("synonyms.empty")}
            </div>
          ) : (
            grouped.map(([domRaw, rs]) => {
              const dom =
                domRaw === "(미분류)" ? t("synonyms.uncategorized") : domRaw;
              return (
                <div key={dom} style={{ marginBottom: 18 }}>
                  <h3
                    style={{
                      margin: 0,
                      marginBottom: 6,
                      fontSize: 12,
                      color: "var(--audit-text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    {dom}{" "}
                    <span
                      style={{
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 12,
                      }}
                    >
                      ({rs.length})
                    </span>
                  </h3>
                  <table
                    style={{
                      width: "100%",
                      fontSize: 12,
                      borderCollapse: "collapse",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "var(--audit-bg-subtle)" }}>
                        <th
                          style={{
                            padding: 6,
                            textAlign: "left",
                            width: 220,
                            borderBottom: "1px solid var(--audit-border)",
                          }}
                        >
                          term
                        </th>
                        <th
                          style={{
                            padding: 6,
                            textAlign: "center",
                            width: 30,
                            borderBottom: "1px solid var(--audit-border)",
                            color: "var(--audit-text-muted)",
                          }}
                        >
                          ↔
                        </th>
                        <th
                          style={{
                            padding: 6,
                            textAlign: "left",
                            width: 220,
                            borderBottom: "1px solid var(--audit-border)",
                          }}
                        >
                          synonym
                        </th>
                        <th
                          style={{
                            padding: 6,
                            textAlign: "left",
                            borderBottom: "1px solid var(--audit-border)",
                          }}
                        >
                          note
                        </th>
                        <th
                          style={{
                            padding: 6,
                            textAlign: "right",
                            width: 130,
                            borderBottom: "1px solid var(--audit-border)",
                          }}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {rs.map((row) =>
                        editing === row.id ? (
                          <tr key={row.id}>
                            <td colSpan={5} style={{ padding: 0 }}>
                              <DraftEditor
                                draft={draft}
                                setDraft={setDraft}
                                domains={domains}
                                onSave={save}
                                onCancel={cancelEdit}
                                saving={saving}
                              />
                            </td>
                          </tr>
                        ) : (
                          <tr
                            key={row.id}
                            style={{
                              borderBottom: "1px solid var(--audit-border)",
                            }}
                          >
                            <td style={{ padding: "5px 6px" }}>{row.term}</td>
                            <td
                              style={{
                                padding: "5px 6px",
                                textAlign: "center",
                                color: "var(--audit-text-muted)",
                              }}
                            >
                              ↔
                            </td>
                            <td style={{ padding: "5px 6px" }}>
                              {row.synonym}
                            </td>
                            <td
                              style={{
                                padding: "5px 6px",
                                color: "var(--audit-text-muted)",
                                fontSize: 12,
                              }}
                            >
                              {row.note || ""}
                            </td>
                            <td
                              style={{ padding: "3px 6px", textAlign: "right" }}
                            >
                              <button
                                className="audit-btn"
                                onClick={() => startEdit(row)}
                                disabled={editing !== null}
                                style={{
                                  fontSize: 12,
                                  padding: "2px 8px",
                                  marginRight: 4,
                                }}
                              >
                                수정
                              </button>
                              <button
                                className="audit-btn"
                                onClick={() => remove(row)}
                                disabled={editing !== null}
                                style={{
                                  fontSize: 12,
                                  padding: "2px 6px",
                                  color: "#d93025",
                                  borderColor: "rgba(217,48,37,0.4)",
                                }}
                                title="삭제"
                              >
                                <Trash2 size={11} />
                              </button>
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
        </div>

        {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
      </div>
    </div>
  );
}

// ─── editor row 컴포넌트 ───
const CANDIDATE_SOURCE = { llm_name: "LLM 품명", llm_name_reduced: "LLM 품명(축약)", llm_name_head: "LLM 품명(서술형의 머리 명사)", typo: "오타 교정", typo_reduced: "오타 교정(축약)", typo_head: "오타 교정(머리 명사)" };

function PendingCandidates({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [available, setAvailable] = useState(true);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(true);
  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/hs-synonyms/candidates?status=pending&limit=200");
      if (!r.ok) { setAvailable(false); return; }
      const j = await r.json();
      setAvailable(j.available !== false);
      setRows(Array.isArray(j.rows) ? j.rows : []);
    } catch (_) { setAvailable(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (row, approve) => {
    setBusy(row.id);
    try {
      const r = await fetch(`/api/hs-synonyms/candidates/${row.id}/${approve ? "approve" : "reject"}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: approve ? JSON.stringify({ domain: "관세율표" }) : undefined });
      if (r.ok) { await load(); if (approve && onChanged) onChanged(); }
    } finally { setBusy(null); }
  };
  if (!available) return null;
  return (
    <section data-testid="synonym-candidates" style={{ margin: "10px 16px 0", border: "1px solid var(--audit-border)", borderRadius: 6, background: "var(--audit-bg-elevated, var(--audit-bg))" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", fontSize: 12 }}>
        <b>승인 대기 후보</b>
        <span style={{ color: "var(--audit-text-muted)" }}>검색에서 자동으로 모은 동의어 쌍(LLM이 읽은 품명 · 오타 교정) · 승인해야 사전에 들어가고 검색에 바로 쓰입니다</span>
        <span style={{ marginLeft: "auto", fontFamily: "JetBrains Mono, monospace" }}>{rows.length}건</span>
        <button type="button" onClick={() => setOpen((v) => !v)} style={{ border: "1px solid var(--audit-border)", background: "transparent", borderRadius: 4, padding: "2px 8px", fontSize: 11.5, cursor: "pointer", fontFamily: "inherit", color: "inherit" }}>{open ? "접기" : "펼치기"}</button>
      </div>
      {open && (rows.length === 0
        ? <div style={{ padding: "0 12px 10px", fontSize: 12, color: "var(--audit-text-muted)" }}>대기 중인 후보가 없습니다.</div>
        : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ color: "var(--audit-text-muted)", fontSize: 11.5 }}>
              <th style={{ textAlign: "left", padding: "4px 12px", fontWeight: 500 }}>검색어(시장어)</th>
              <th style={{ textAlign: "left", padding: "4px 12px", fontWeight: 500 }}>사전·법령어</th>
              <th style={{ textAlign: "left", padding: "4px 12px", fontWeight: 500 }}>출처</th>
              <th style={{ textAlign: "right", padding: "4px 12px", fontWeight: 500 }}>관측</th>
              <th style={{ textAlign: "left", padding: "4px 12px", fontWeight: 500 }}>최근</th>
              <th style={{ padding: "4px 12px" }} />
            </tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-testid="synonym-candidate" style={{ borderTop: "1px solid var(--audit-border)" }}>
                  <td style={{ padding: "6px 12px", fontWeight: 600 }}>
                    {row.term}
                    {/* 49차: 서술형 검색어의 머리 명사 후보는 원문을 함께 보여 관리자가 머리 명사가 맞는지 판단한다 */}
                    {String(row.source || "").endsWith("_head") && row.evidence && (
                      <div data-testid="synonym-candidate-origin" style={{ fontSize: 11, fontWeight: 400, color: "var(--audit-text-muted)", marginTop: 2, maxWidth: 520, overflowWrap: "anywhere" }}>{row.evidence}</div>
                    )}
                  </td>
                  <td style={{ padding: "6px 12px" }}>{row.synonym}</td>
                  <td style={{ padding: "6px 12px", color: "var(--audit-text-muted)" }} title={row.evidence || ""}>{CANDIDATE_SOURCE[row.source] || row.source}</td>
                  <td style={{ padding: "6px 12px", textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{row.count}</td>
                  <td style={{ padding: "6px 12px", color: "var(--audit-text-muted)", whiteSpace: "nowrap" }}>{String(row.last_seen || "").slice(0, 16)}</td>
                  <td style={{ padding: "6px 12px", whiteSpace: "nowrap", textAlign: "right" }}>
                    <button type="button" disabled={busy === row.id} onClick={() => decide(row, true)} style={{ border: "1px solid #2563eb", background: "#2563eb", color: "#fff", borderRadius: 4, padding: "2px 10px", fontSize: 11.5, cursor: "pointer", fontFamily: "inherit", marginRight: 6 }}>승인</button>
                    <button type="button" disabled={busy === row.id} onClick={() => decide(row, false)} style={{ border: "1px solid var(--audit-border)", background: "transparent", color: "var(--audit-text-muted)", borderRadius: 4, padding: "2px 10px", fontSize: 11.5, cursor: "pointer", fontFamily: "inherit" }}>거절</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
    </section>
  );
}

function DraftEditor({
  draft,
  setDraft,
  domains,
  onSave,
  onCancel,
  saving,
  isNew = false,
}) {
  return (
    <div
      style={{
        padding: 10,
        background: "var(--audit-accent-bg)",
        border: "1px solid var(--audit-accent)",
        borderRadius: 6,
        display: "flex",
        gap: 6,
        alignItems: "flex-start",
        marginBottom: isNew ? 18 : 0,
      }}
    >
      <input
        autoFocus
        value={draft.term}
        onChange={(e) => setDraft({ ...draft, term: e.target.value })}
        placeholder="term (한글/영문)"
        style={inputStyle(180)}
      />
      <span
        style={{
          color: "var(--audit-text-muted)",
          padding: "5px 4px",
          fontSize: 13,
        }}
      >
        ↔
      </span>
      <input
        value={draft.synonym}
        onChange={(e) => setDraft({ ...draft, synonym: e.target.value })}
        placeholder="synonym (반대 표현)"
        style={inputStyle(180)}
      />
      <input
        value={draft.domain}
        onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
        placeholder="domain (예: 42-가죽)"
        list="domain-suggest"
        style={inputStyle(120)}
      />
      <datalist id="domain-suggest">
        {domains.map((d) => (
          <option key={d} value={d} />
        ))}
      </datalist>
      <input
        value={draft.note}
        onChange={(e) => setDraft({ ...draft, note: e.target.value })}
        placeholder="note (선택)"
        style={{ ...inputStyle(200), flex: 1 }}
      />
      <button
        className="audit-btn audit-btn-primary"
        onClick={onSave}
        disabled={saving || !draft.term.trim() || !draft.synonym.trim()}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontSize: 12,
          padding: "5px 10px",
        }}
      >
        <Save size={11} /> {isNew ? "추가" : "저장"}
      </button>
      <button
        className="audit-btn"
        onClick={onCancel}
        disabled={saving}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          fontSize: 12,
          padding: "5px 8px",
        }}
      >
        <X size={11} /> 취소
      </button>
    </div>
  );
}

function inputStyle(width) {
  return {
    width,
    padding: "5px 8px",
    fontSize: 12,
    border: "1px solid var(--audit-border-strong)",
    background: "var(--audit-bg)",
    color: "var(--audit-text)",
    borderRadius: 4,
    fontFamily: "inherit",
  };
}

function HelpModal({ onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        className="audit-root"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--audit-bg-elevated)",
          color: "var(--audit-text)",
          border: "1px solid var(--audit-border-strong)",
          borderRadius: 8,
          maxWidth: 680,
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          padding: 24,
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "var(--audit-text-muted)",
            padding: 4,
          }}
        >
          <X size={18} />
        </button>
        <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18 }}>
          동의어 사전 도움말
        </h2>
        <section style={{ marginBottom: 16, fontSize: 12, lineHeight: 1.7 }}>
          <h3
            style={{
              fontSize: 14,
              color: "var(--audit-accent)",
              marginBottom: 4,
            }}
          >
            동의어 매칭 동작 방식
          </h3>
          <ul style={{ paddingLeft: 18 }}>
            <li>
              <strong>양방향</strong>: <code>handbag ↔ 핸드백</code> 한 행만
              추가하면 양쪽 모두 매치 (pgroonga 가 term/synonym 양 컬럼 lookup).
            </li>
            <li>
              <strong>FTS 만 적용</strong>: /분류사례 탐색 정밀 검색 ON 시에만
              자동 expansion. 클라이언트 substring 매치에는 영향 없음.
            </li>
            <li>
              <strong>도메인 (domain)</strong>: 관리/필터용 라벨 (예{" "}
              <code>42-가죽</code>, <code>33-화장품</code>). 검색에는 영향 없음.
            </li>
            <li>
              <strong>note</strong>: 출처/근거 메모 (예 "관세사가 직접 등록 —
              2026-05").
            </li>
          </ul>
        </section>
        <section style={{ marginBottom: 16, fontSize: 12, lineHeight: 1.7 }}>
          <h3
            style={{
              fontSize: 14,
              color: "var(--audit-accent)",
              marginBottom: 4,
            }}
          >
            등록 예시
          </h3>
          <table style={{ width: "100%", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--audit-bg-subtle)" }}>
                <th style={{ textAlign: "left", padding: 4 }}>term</th>
                <th style={{ textAlign: "left", padding: 4 }}>synonym</th>
                <th style={{ textAlign: "left", padding: 4 }}>domain</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: 4 }}>handbag</td>
                <td style={{ padding: 4 }}>핸드백</td>
                <td style={{ padding: 4 }}>42-가죽</td>
              </tr>
              <tr>
                <td style={{ padding: 4 }}>GRI 3</td>
                <td style={{ padding: 4 }}>통칙 3</td>
                <td style={{ padding: 4 }}>법령</td>
              </tr>
              <tr>
                <td style={{ padding: 4 }}>cosmetic</td>
                <td style={{ padding: 4 }}>화장품</td>
                <td style={{ padding: 4 }}>33-화장품</td>
              </tr>
            </tbody>
          </table>
        </section>
        <section style={{ fontSize: 12, color: "var(--audit-text-muted)" }}>
          ※ 추가/수정/삭제는 즉시 반영됩니다. /분류사례 탐색의 다음 FTS 쿼리부터
          새 동의어가 expansion 됩니다.
        </section>
      </div>
    </div>
  );
}
